import React, { useCallback, useEffect, useState } from 'react';
import { supportingDataService, SupportingDataItem } from '../../../../services/supportingDataService';
import { DataTable, DataTableColumn } from '../../shared/DataTable';
import { CtaRegistryEditor } from './CtaRegistryEditor';
import { IMAGE_MODEL_OPTIONS } from '../../../../constants/imageModels';

// ---------------------------------------------------------------------------
// Supporting Data List
// ---------------------------------------------------------------------------

type AssetRow = SupportingDataItem & { key: string };

const COLUMNS: DataTableColumn<AssetRow>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (row) => (
      <>
        <div className="font-medium text-gray-900">{row.name}</div>
        <div className="text-xs text-gray-400 mt-0.5">{row.group}</div>
      </>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    render: (row) => <span className="text-gray-500">{row.description}</span>,
  },
  {
    key: 'type',
    header: 'Type',
    className: 'w-24',
    render: (row) => <span className="text-gray-400 font-mono text-xs uppercase">{row.editor_type}</span>,
  },
];

function SupportingDataList({ onSelect }: { onSelect: (item: SupportingDataItem) => void }) {
  const [items, setItems] = useState<AssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await supportingDataService.list();
        if (!cancelled) setItems((resp.assets || []).map((a) => ({ ...a, key: a.asset_id })));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load supporting data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={items}
      onRowClick={(row) => onSelect(row)}
      emptyMessage="No supporting data found."
    />
  );
}

// ---------------------------------------------------------------------------
// Supporting Data Editor
// ---------------------------------------------------------------------------

function SupportingDataEditor({ item, onBack }: {
  item: SupportingDataItem;
  onBack: () => void;
}) {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await supportingDataService.get(item.asset_id);
        if (cancelled) return;
        const displayContent = item.editor_type === 'json' && data.content
          ? tryPrettyPrint(data.content)
          : data.content;
        setContent(displayContent);
        setOriginalContent(displayContent);
        setEtag(data.etag);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load content');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item.asset_id]);

  const isDirty = content !== originalContent;

  const validateContent = useCallback((): boolean => {
    if (item.editor_type === 'json') {
      try {
        JSON.parse(content);
        setJsonError(null);
        return true;
      } catch (e: any) {
        setJsonError(`Invalid JSON: ${e.message}`);
        return false;
      }
    }
    return true;
  }, [content, item.editor_type]);

  const handleSave = useCallback(async () => {
    if (!validateContent()) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const result = await supportingDataService.update(item.asset_id, content, etag);
      setEtag(result.etag);
      setOriginalContent(content);
      setSavedMessage('Saved successfully');
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [content, etag, item.asset_id, validateContent]);

  const handleDiscard = () => {
    setContent(originalContent);
    setJsonError(null);
    setError(null);
    setSavedMessage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-block">
          &larr; Back to supporting data
        </button>
        <h2 className="text-lg font-semibold text-gray-900">{item.name}</h2>
        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
      </div>

      <div className="bg-white border rounded p-4">
        {item.editor_type === 'model_select' ? (
          <select
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Pipeline default (Imagen 4.0 Ultra)</option>
            {IMAGE_MODEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label} — {o.provider}</option>
            ))}
          </select>
        ) : (
          <textarea
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (jsonError) setJsonError(null);
            }}
            rows={item.editor_type === 'json' ? 25 : 20}
            className={`w-full px-3 py-2 text-sm font-mono border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y ${
              jsonError ? 'border-red-300' : 'border-gray-300'
            }`}
          />
        )}
        {jsonError && <p className="text-sm text-red-600 mt-2">{jsonError}</p>}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={handleDiscard} disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          Discard changes
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Changes affect all future article generation runs immediately.</span>
      </div>
    </div>
  );
}

function tryPrettyPrint(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

// ---------------------------------------------------------------------------
// Section root
// ---------------------------------------------------------------------------

export const SupportingDataManagement: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<SupportingDataItem | null>(null);

  if (selectedItem) {
    if (selectedItem.editor_type === 'cta_registry') {
      return <CtaRegistryEditor item={selectedItem} onBack={() => setSelectedItem(null)} />;
    }
    return <SupportingDataEditor item={selectedItem} onBack={() => setSelectedItem(null)} />;
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900 mb-6">Supporting Data</h1>
      <p className="text-sm text-gray-500 mb-4">
        Edit the shared configuration documents that feed into the article generation pipeline.
      </p>
      <SupportingDataList onSelect={setSelectedItem} />
    </div>
  );
};
